<script lang="ts" setup>
import { computed, CSSProperties, onBeforeUnmount, onMounted, Ref, ref, watch, watchEffect } from "vue";
import { useWindowSize, watchDebounced } from "@vueuse/core";
import {
    BadgeModel,
    DateFormatEnum,
    devLogger,
    error,
    Exception,
    exception,
    getAboutInfoModel,
    ImageFileModel,
    info,
    isMeshFileFormat,
    MContainer,
    MContainerColumn,
    MContainerGrow,
    MContainerRow,
    MLoading,
    RgSubTitle,
    RgTitle,
    success,
    toUserDashboard,
    useAuth,
    useServerApi,
    useUtilStore,
    UtilModel,
    warn,
    useNavbar,
    NavbarNamedEvents,
    NavbarEventBus,
    toUserForgetPassoword,
    toVideoTutorial,
    toRemoteSupport,
    toWeblinkPcSelection,
    useUserStore,
    openSuppportTicket,
    ButtonEnum,
    useAppSettings,
    WeblinkFileUploadEmitModel,
} from "@xlabmedical/medic-web-framework";
import { ChangeBackground } from "@/util/PatientListHelpers";
import { DoctorModel, NotificationChannelType, ProjectFileType, ProjectShareModel, ResponseStatusEnum, FileElement, User, WebCADModule } from "@xlabmedical/medic-api";
import { GlobalConstants, isDevelopment } from "@/constants/GlobalConstants";
import { usePatientsStore } from "@/stores/PatientsStore";
import { useRoute, useRouter } from "vue-router";
import { IPAboutInfoComponent, ProjectModel } from "@xlabmedical/implant-planner";
import { LoadPatientListSlimModel, PatientListModel } from "@/models/PatientListModel";
import { useDialog } from "primevue/usedialog";
import { useConfirm } from "primevue/useconfirm";
import AddPatientDialog, { formData } from "@/components/RgAddPatientComponent.vue";
import ImportProjectDialog from "@/components/RgImportProjectComponent.vue";
import EditPatientDialog from "@/components/RgEditPatientComponent.vue";
import { PiniaCustomStateProperties, storeToRefs } from "pinia";
import { NamedRoutes, redirectToLogin } from "@/router/WebRgRouter";
import RgPatientListHeader from "@/components/RgPatientListHeader.vue";
import RgButtonMenu from "@/components/RgButtonMenu.vue";
import { downloadFile, getSegmentationResult, takeStlFileScreenshot } from "@xlabmedical/medic-web-engine";
import ProgressBar from "primevue/progressbar";
import { useI18n } from "vue-i18n";
import { useProductionCenterStore } from "@/stores/ProductionCenterStore";
import RgNotificationSelectionTab from "@/components/RgNotificationSelectionTab.vue";
import { ChatMessageModel } from "@/models/ChatMessageModel";
import { ExportFileModel } from "@/models/ExportFileModel";
import RgProjectList from "@/components/ProjectItem/RgProjectList.vue";
import RgFileListView from "@/components/RgFileListView.vue";
import RgShareProjectComponent from "@/components/RgShareProjectComponent.vue";
import Dialog from "primevue/dialog";
import ConfirmDialog from "primevue/confirmdialog";
import { SegmentationStatus } from "@/models/SegmentationStatus";
import RgPatientList from "@/components/RgPatientList.vue";
import { GetNavbarModel } from "@/util/NavbarUtil";
import JSZip from "jszip";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        exportToAlfred?: boolean;
    }>(),
    {
        exportToAlfred: false,
    },
);

const userStore = useUserStore();
const productionCenterStore = useProductionCenterStore();

const navbar = useNavbar();
const { hasOpenTicket, hasRemoteSupport } = useAppSettings();

const dialog = useDialog();
const api = useServerApi();

const showPatientList = ref(true);

const loadingFlag: Ref<boolean> = ref(false);
const progressBarFlag: Ref<boolean> = ref(false);
const progressBarValue: Ref<number> = ref(0);
const progressBarRefresh: Ref<number> = ref(Math.random());
const loadingText: Ref<string> = ref(t("shared.loading"));
const forceUpdateChat = ref(Math.random() * 100);

const tabIndex = ref(0);
const selectedOrderId = ref<string | undefined>(undefined);

const divFlag = ref(false);
const loading = ref(true);
const loadingAttachemnts = ref(false);
const uploadingAttachment = ref(false);
const lastAttachmentsPatientId = ref<string | null>(null);
const lastAttachmentsTabIndex = ref<number | null>(null);
const attachmentsLoadRequestId = ref(0);

const confirm = useConfirm();

const route = useRoute();
const router = useRouter();

const routeName = ref(route.name);

const auth = useAuth();

const patientsStore = usePatientsStore();
const {
    patients,
    doctors,
    selectedPatientFull,
    selectedPatientFileModel,
    selectedPatientProjects,
    selectedPatientNotifications,
    projectsModel,
    totalPatientCount,
    patientListQuery,
    legacyProjectsModel,
} = storeToRefs(patientsStore);

const userSettingsStore = useUserSettingsStore();

const mainProject = ref<ProjectModel | null>(null);
const selectedPatientDoctor = ref<DoctorModel | undefined>(undefined);
const changeSelectedPatient = ref<PatientListModel | undefined>(undefined);
const files = reactive({
    projects: [] as ProjectModel[],
});

const utilStore = useUtilStore();
const isMobile = ref(utilStore.isMobileView);
const hideGrid = ref(!!isMobile.value);
const forceRerenderProjects = ref(0);
const isSmartphoneUserAgent = computed(() => {
    if (typeof navigator === "undefined") {
        return false;
    }
    return /Android.*Mobile|iPhone|iPod|Windows Phone|IEMobile|BlackBerry|Opera Mini/i.test(navigator.userAgent);
});
const isSmartphoneMode = computed(() => isSmartphoneUserAgent.value && isMobile.value);
const smartphoneUnsupportedNoticeDismissed = ref(false);
const showSmartphoneUnsupportedNotice = computed(() => {
    return isSmartphoneMode.value && !smartphoneUnsupportedNoticeDismissed.value;
});
const dismissSmartphoneUnsupportedNotice = () => {
    smartphoneUnsupportedNoticeDismissed.value = true;
};

const username = computed(() => user.value?.username);
const license = computed(() => user.value?.license);

const dateFormat = DateFormatEnum.EU;

const exporting = ref(false);
const exportingClosed = ref(false);
const exportingText = ref("");
const { width } = useWindowSize();
const exportDialogWidth = computed(() => (width.value > 1850 ? "40vw" : "50vw"));

const searchString = ref("");
const selectMultiple = ref(false);
const selectedMultiplePatients = ref<PatientListModel[]>([]);
const filterDateRange = ref<[Date | null, Date | null] | null>(null);

watch(isMobile, (mobile) => {
    if (!mobile || !selectMultiple.value) {
        return;
    }

    selectedMultiplePatients.value = [];
    selectMultiple.value = false;
});

let selectedPatient: PatientListModel | null = null;

const selectFirst = async () => {
    await patientSelectionChanged(patients.value[0]);

    selectedPatientArray.value = patients.value[0];
};

const hasLegacyProject = computed(() => {
    return selectedPatientArray.value?.hasLegacyProject;
});

const onShareClicked = () => {
    dialog.open(RgShareProjectComponent, {
        props: {
            header: t("shared.sharePatient"),
            style: { width: "50rem" },
            modal: true,
            dismissableMask: true,
        },
        data: {
            isFileShare: false,
        },
        emits: {
            onShareProject: async (message: string, username: string, editable: boolean, anonymize: boolean) => {
                const data = {
                    projectName: mainProject.value?.patientName,
                    withUsername: username,
                    message: message,
                    editable: editable,
                    anonymize: anonymize,
                } as ProjectShareModel;

                const shareResponse = await patientsStore.shareProject(data);

                if (shareResponse.success) {
                    success(t("web-rg.successfullySharedProject"));
                } else {
                    error(t("web-rg.errorWhileSharingProject"));
                }
            },
        },
    });
};

const onDelete = () => {
    if (selectMultiple.value && selectedMultiplePatients.value.length > 1) {
        onDeleteBatch();
        return;
    }
    const patientLabel = selectedPatient?.firstName ? `${patientNameFromSelection} (${selectedPatient.firstName})` : patientNameFromSelection;
    const message = t("web-rg.deleteStudyConfirm", { patientLabel }).replace(/\\n/g, "\n");
    confirm.require({
        group: "deleteStudy",
        message,
        header: t("web-rg.deleteStudy"),
        icon: "pi pi-exclamation-triangle",
        acceptIcon: "pi pi-check",
        rejectIcon: "pi pi-times",
        accept: async () => {
            await api.deleteProject(patientNameFromSelection);
            info(t("web-rg.studyDeletedMessage", { patientLabel }), t("web-rg.studyDeleted"));
            confirm.close();

            await patientsStore.loadPatientsSlim();

            if (patients.value.length === 0) {
                selectedPatientProjects.value = [];
            }

            await selectFirst();
        },
        reject: () => {
            confirm.close();
        },
    });
};

const onDeleteBatch = () => {
    const selectedPatients = selectedMultiplePatients.value.filter((p) => p.patientName);
    if (selectedPatients.length < 2) {
        onDelete();
        return;
    }
    const patientNames = selectedPatients.map((p) => p.patientName);
    const message = t("web-rg.deleteStudiesConfirm", { count: patientNames.length }).replace(/\\n/g, "\n");
    confirm.require({
        group: "deleteStudy",
        message,
        header: t("web-rg.deleteStudies"),
        icon: "pi pi-exclamation-triangle",
        acceptIcon: "pi pi-check",
        rejectIcon: "pi pi-times",
        accept: async () => {
            await patientsStore.deleteProjectBatch(patientNames);
            const patientLabel = `${patientNames.length} patients`;
            info(t("web-rg.studiesDeletedMessage", { patientLabel }), t("web-rg.studiesDeleted"));
            confirm.close();

            await patientsStore.loadPatientsSlim();

            selectMultiple.value = false;
            selectedMultiplePatients.value = [];

            if (patients.value.length === 0) {
                selectedPatientProjects.value = [];
            }
        },
        reject: () => {
            confirm.close();
        },
    });
};

const onDeleteAttachment = (item: ImageFileModel) => {
    const patientName = item.patientId || selectedPatient?.patientName || "";
    if (!patientName) {
        warn(t("web-rg.missingPatientNameForDeletion"));
        return;
    }
    confirm.require({
        group: "deleteProjectFile",
        message: t("web-rg.deleteProjectFilePrompt", { filename: item.filename }),
        header: t("shared.delete"),
        icon: "pi pi-exclamation-triangle",
        accept: async () => {
            await patientsStore.deleteProjectFile(patientName, item.filename);
            await patientsStore.loadPatientAttachments(patientName);
            confirm.close();
        },
        reject: () => {
            confirm.close();
        },
    });
};

const onPatientListFiltersCleared = async () => {
    await patientsStore.loadPatientsSlim({ page: 0, modifiedFrom: null, modifiedTo: null });
};

const toIsoRange = (range: [Date | null, Date | null] | null) => {
    if (!range) {
        return { modifiedFrom: null, modifiedTo: null };
    }

    const [from, to] = range;

    return {
        modifiedFrom: from ? new Date(from.setHours(0, 0, 0, 0)).toISOString() : null,
        modifiedTo: to ? new Date(to.setHours(23, 59, 59, 999)).toISOString() : null,
    };
};

const debouncedFilterRange = ref<[Date | null, Date | null] | null>(null);

watchDebounced(
    () => filterDateRange.value,
    (range) => {
        debouncedFilterRange.value = range;
    },
    { debounce: 400, maxWait: 1000 },
);

watch(
    () => debouncedFilterRange.value,
    async (range) => {
        const { modifiedFrom, modifiedTo } = toIsoRange(range);
        if (!modifiedFrom || !modifiedTo) {
            return;
        }
        await patientsStore.loadPatientsSlim({ page: 0, modifiedFrom, modifiedTo });
    },
);

const next = async (data: formData) => {
    await importData(data, true);
};

const onPatientListPageChange = async ({ page, rows }: { page: number; rows: number }) => {
    await patientsStore.loadPatientsSlim({ page, itemsPerPage: rows });
};

const onPatientListSortChange = async ({
    page,
    rows,
    sortColumn,
    sortDirection,
}: {
    page: number;
    rows: number;
    sortColumn: LoadPatientListSlimModel["sortColumn"];
    sortDirection: LoadPatientListSlimModel["sortDirection"];
}) => {
    await patientsStore.loadPatientsSlim({ page, itemsPerPage: rows, sortColumn, sortDirection });
};

const onPatientListPageSizeChange = async (rows: number) => {
    if (patientsStore.patientListQuery.itemsPerPage === rows) {
        return;
    }
    await patientsStore.loadPatientsSlim({ itemsPerPage: rows, page: 0 });
};

const onPatientListSearchChange = async (search: string) => {
    await patientsStore.loadPatientsSlim({ search, page: 0 });
};

const vtkContainer = ref();

const updateProj = async () => {
    if (selectedPatient) {
        await patientsStore.loadPatientsSlim();
        await patientSelectionChanged(selectedPatient);
        await updateProjectsPromise(selectedPatient.patientId ?? "");
    }
};

const onOrderNotificationsClicked = (orderId: string) => {
    selectedOrderId.value = orderId;
    tabIndex.value = 4;
    forceUpdateChat.value = Math.random() * 100;
};

const handleSegmentationTriggered = async (projectId: string) => {
    loadingFlag.value = true;
    const project = projectsModel.value.find((p: ProjectModel) => p.id === projectId);
    const pList = await api.getZippedFilesList(project.patientName, project.projectFilename);

    if (!project || !pList.data.length) {
        devLogger.error("Project not found");
        loadingFlag.value = false;
        return;
    }

    const projectName = project.projectFilename.replace(".zip", "");

    confirm.require({
        message: t("web-rg.triggerSegmentationConfirm", { projectName }),
        header: t("web-rg.triggerSegmentation"),
        accept: async () => {
            let newStatus = SegmentationStatus.NOT_STARTED;
            try {
                let segmentationStatus = await getSegmentationResult(api, project.projectFileUuid);
                let segmentationTriggered = false;
                if (!segmentationStatus || segmentationStatus.jobUuid === "") {
                    const response = await api.triggerSegmentationJob(project.projectFileUuid);
                    if (response.status !== ResponseStatusEnum.OK) {
                        error(t("web-rg.errorAtTriggeringSegmentationStatus"));
                    } else {
                        newStatus = SegmentationStatus.IN_PROCESS;
                        segmentationTriggered = true;
                    }
                } else if (segmentationStatus) {
                    newStatus = segmentationStatus.completed ? SegmentationStatus.COMPLETED : SegmentationStatus.IN_PROCESS;
                }
                if (segmentationTriggered) {
                    info(`${t("web-rg.youHaveTriggeredSegmentationForProject")}: ${projectName}`, t("web-rg.segmentationTriggered"));
                }
            } catch (err) {
                error(t("web-rg.errorAtTriggeringSegmentationStatus"));
                devLogger.warn("Error at triggering segmentation status", err.response);
            }
            try {
                const invokedByUser = true;
                patientsStore.updateProjectSegmentationStatus(project.projectFileUuid, newStatus, invokedByUser);
            } catch (err) {
                error(t("web-rg.errorAtUpdatingSegmentationStatus"));
                devLogger.error("Error at updating segmentation status", err);
            }
            confirm.close();
            loadingFlag.value = false;
        },
        reject: () => {
            confirm.close();
            loadingFlag.value = false;
        },
    });
};

function exportToAlfredTriggered(projectId: string) {
    const project = files.projects.find((p: ProjectModel) => p.id === projectId);
    if (!project) {
        devLogger.error("Project not found");
        return;
    }
    confirm.require({
        message: t("web-rg.exportToAlfredPrompt"),
        header: t("web-rg.exportToAlfred"),
        accept: async () => {
            await router.push({
                name: "ImplantPlannerProjectLoaderViewAlfred",
                params: {
                    projectHash: project.uuid,
                    alfredId: `${route.query.alfred_patient_id}`,
                },
            });
        },
    });
}

function exportClosedClicked(value: boolean) {
    if (!value) exportingClosed.value = true;
}

async function downloadAll(filesForDownload: { fileUuid: string; downloadFilename: string }[]) {
    const total = filesForDownload.length;

    if (total === 0) {
        exporting.value = false;
        progressBarValue.value = 0;
        exportingText.value = t("shared.noFilesToDownload");
        return;
    }

    exporting.value = true;
    progressBarValue.value = 0;
    exportingText.value = t("web-rg.downloadStarting", { current: 0, total, percent: 0 });

    for (const [i, model] of filesForDownload.entries()) {
        const fileIndex = i + 1;

        // Baseline (completed files only)
        const baselinePct = Math.floor((i / total) * 100);
        progressBarValue.value = Math.min(99, baselinePct);
        exportingText.value = t("web-rg.downloadPreparing", {
            filename: model.downloadFilename,
            current: fileIndex,
            total,
            percent: baselinePct,
        });

        // Throttled progress from the API (progress is 0..1)
        const onProgress = useThrottleFn((progress: number) => {
            const p = Math.max(0, Math.min(1, progress || 0));
            const overallPct = Math.floor(((i + p) / total) * 100);
            progressBarValue.value = Math.min(99, overallPct);
            exportingText.value = t("web-rg.downloadProgress", {
                filename: model.downloadFilename,
                current: fileIndex,
                total,
                percent: overallPct,
            });
        }, 250);

        try {
            const response = await api.GetFileByUUID(model.fileUuid, onProgress);
            const blob = new Blob([response.data], { type: "application/zip" });
            if (!exportingClosed.value) {
                downloadFile(model.downloadFilename, blob);
            } else {
                break;
            }

            // Snap to "this file done"
            const afterPct = Math.floor(((i + 1) / total) * 100);
            progressBarValue.value = Math.min(99, afterPct);
            exportingText.value = t("web-rg.downloadedFile", {
                filename: model.downloadFilename,
                current: fileIndex,
                total,
                percent: afterPct,
            });
        } catch (err) {
            devLogger.error("Failed to download:", model.fileUuid, err);
            // Keep going, but reflect failure in the text
            const afterPct = Math.floor(((i + 1) / total) * 100);
            progressBarValue.value = Math.min(99, afterPct);
            exportingText.value = t("web-rg.downloadFailed", {
                filename: model.downloadFilename,
                current: fileIndex,
                total,
                percent: afterPct,
            });
        }
    }

    progressBarValue.value = 100;
    exportingText.value = t("web-rg.downloadComplete", {
        completed: filesForDownload.length,
        total: filesForDownload.length,
        percent: 100,
    });
    exporting.value = false;
}

const sanitizeExportName = (name: string) => {
    const cleaned = name
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .trim();
    return cleaned.length > 0 ? cleaned : t("shared.patient");
};

const getPatientExportBaseName = (patient: PatientListModel) => {
    const fullName = `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim();
    return sanitizeExportName(fullName || patient.patientName || t("shared.patient"));
};

const buildPatientExportTargets = (patientsList: PatientListModel[]) => {
    const baseNames = patientsList.map(getPatientExportBaseName);
    const counts = new Map<string, number>();
    baseNames.forEach((name) => counts.set(name, (counts.get(name) ?? 0) + 1));
    const nextIndex = new Map<string, number>();

    return patientsList.map((patient, idx) => {
        const baseName = baseNames[idx];
        if ((counts.get(baseName) ?? 0) > 1) {
            const index = (nextIndex.get(baseName) ?? 0) + 1;
            nextIndex.set(baseName, index);
            return { patient, filename: `${baseName}_${index}.zip` };
        }
        return { patient, filename: `${baseName}.zip` };
    });
};

const getMultiPatientExportFilename = () => {
    const now = new Date();
    const pad = (value: number) => value.toString().padStart(2, "0");
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
    const patientsLabel = sanitizeExportName(t("shared.patients"));
    const exportLabel = sanitizeExportName(t("shared.export"));
    return `${patientsLabel}_${exportLabel}_${timestamp}.zip`;
};

type MultiExportSaveTarget = {
    filename: string;
    fileHandle?: any;
};

const requestMultiExportSaveTarget = async (filename: string): Promise<MultiExportSaveTarget | null> => {
    if (typeof window === "undefined") {
        return null;
    }

    const showSaveFilePicker = (window as Window & { showSaveFilePicker?: (options?: unknown) => Promise<any> }).showSaveFilePicker;

    if (typeof showSaveFilePicker !== "function") {
        return { filename };
    }

    try {
        const fileHandle = await showSaveFilePicker({
            suggestedName: filename,
            types: [
                {
                    description: "ZIP archive",
                    accept: {
                        "application/zip": [".zip"],
                    },
                },
            ],
        });
        return { filename, fileHandle };
    } catch (err: any) {
        if (err?.name === "AbortError") {
            return null;
        }

        devLogger.warn("Save dialog failed, falling back to default download", err);
        return { filename };
    }
};

const saveBlobToMultiExportTarget = async (target: MultiExportSaveTarget, data: Blob): Promise<"saved" | "downloaded" | "cancelled"> => {
    if (!target.fileHandle) {
        downloadFile(target.filename, data);
        return "downloaded";
    }

    try {
        const writable = await target.fileHandle.createWritable();
        try {
            await writable.write(data);
            await writable.close();
            return "saved";
        } catch (err: any) {
            try {
                await writable.abort?.();
            } catch {
                // ignore abort failures
            }
            if (err?.name === "AbortError") {
                return "cancelled";
            }
            throw err;
        }
    } catch (err: any) {
        if (err?.name === "AbortError") {
            return "cancelled";
        }

        devLogger.warn("Writing to selected file failed, falling back to default download", err);
        downloadFile(target.filename, data);
        return "downloaded";
    }
};

async function downloadSelectedPatientsAsZips(patientsList: PatientListModel[], saveTarget: MultiExportSaveTarget) {
    if (!patientsList.length) {
        exportingText.value = t("shared.noFilesToDownload");
        return;
    }

    const targets = buildPatientExportTargets(patientsList);
    const totalPatients = targets.length;
    const outerZip = new JSZip();
    let packagedPatients = 0;
    let patientIndex = 0;

    for (const target of targets) {
        if (exportingClosed.value) {
            break;
        }

        patientIndex += 1;
        const patientId = Number(target.patient.patientId);
        if (!patientId) {
            exportingText.value = t("web-rg.patientZipInvalidId", {
                filename: target.filename,
                current: patientIndex,
                total: totalPatients,
            });
            continue;
        }

        let patientResponse;
        try {
            patientResponse = await api.getPatientFullById(patientId);
        } catch (err) {
            devLogger.error("Failed to load patient for export:", patientId, err);
            exportingText.value = t("web-rg.patientZipLoadFailed", {
                filename: target.filename,
                current: patientIndex,
                total: totalPatients,
            });
            continue;
        }

        const exportableFiles = patientResponse.data.files.filter((file: FileElement) => !file.deleted && file.type === ProjectFileType.Project);

        if (exportableFiles.length === 0) {
            exportingText.value = t("web-rg.patientZipNoFiles", {
                filename: target.filename,
                current: patientIndex,
                total: totalPatients,
            });
            continue;
        }

        const zip = new JSZip();
        const totalFiles = exportableFiles.length;

        for (let i = 0; i < exportableFiles.length; i += 1) {
            if (exportingClosed.value) {
                break;
            }

            const file = exportableFiles[i];
            const fileIndex = i + 1;
            const baselinePct = Math.floor((i / totalFiles) * 100);
            exportingText.value = t("web-rg.patientZipPreparing", {
                filename: target.filename,
                currentPatient: patientIndex,
                totalPatients,
                percent: baselinePct,
            });

            const onProgress = (progress: number = 0) => {
                const p = Math.max(0, Math.max(1, progress - 1 || 0));
                const overallPct = Math.floor(((i + p) / totalFiles) * 100);

                exportingText.value = t("web-rg.patientZipPackaging", {
                    filename: target.filename,
                    currentPatient: patientIndex,
                    totalPatients,
                    percent: overallPct,
                });
            };

            try {
                const response = await api.GetFileByUUID(file.uuid, onProgress);
                zip.file(file.name || `file_${fileIndex}.zip`, response.data);
            } catch (err) {
                devLogger.error("Failed to download patient file:", file.uuid, err);
                exportingText.value = t("web-rg.patientZipFileFailed", {
                    filename: target.filename,
                    currentPatient: patientIndex,
                    totalPatients,
                });
            }
        }

        if (exportingClosed.value) {
            break;
        }

        const zipBlob = await zip.generateAsync(
            {
                type: "blob",
                compression: "DEFLATE",
                compressionOptions: { level: 6 },
            },
            (metadata) => {
                const pct = Math.floor(metadata.percent || 0);
                exportingText.value = t("web-rg.patientZipDownloading", {
                    filename: target.filename,
                    currentPatient: patientIndex,
                    totalPatients,
                    percent: pct,
                });
            },
        );

        if (exportingClosed.value) {
            break;
        }

        outerZip.file(target.filename, zipBlob);
        packagedPatients += 1;
        exportingText.value = t("web-rg.patientZipDownloaded", {
            filename: target.filename,
            currentPatient: patientIndex,
            totalPatients,
        });
    }

    if (exportingClosed.value) {
        return;
    }

    if (packagedPatients === 0) {
        exportingText.value = t("shared.noFilesToDownload");
        return;
    }

    exportingText.value = t("web-rg.downloadPreparing", { filename: saveTarget.filename, current: 1, total: 1, percent: 0 });

    const outerZipBlob = await outerZip.generateAsync(
        {
            type: "blob",
            compression: "DEFLATE",
            compressionOptions: { level: 6 },
        },
        (metadata) => {
            const pct = Math.floor(metadata.percent || 0);
            exportingText.value = t("web-rg.downloadProgress", {
                filename: saveTarget.filename,
                current: 1,
                total: 1,
                percent: pct,
            });
        },
    );

    if (exportingClosed.value) {
        return;
    }

    const saveResult = await saveBlobToMultiExportTarget(saveTarget, outerZipBlob);
    if (saveResult !== "cancelled") {
        exportingText.value = t("web-rg.downloadedFile", { filename: saveTarget.filename, current: 1, total: 1, percent: 100 });
        success(t("web-rg.downloadedFile", { filename: saveTarget.filename, current: 1, total: 1, percent: 100 }));
    }
}

const exportClicked = async (model?: ExportFileModel) => {
    exportingClosed.value = false;
    exporting.value = true;
    exportingText.value = t("shared.loading");

    if (!model) {
        if (selectMultiple.value) {
            const multiPatientFilename = getMultiPatientExportFilename();
            const saveTarget = await requestMultiExportSaveTarget(multiPatientFilename);
            if (!saveTarget) {
                exportingText.value = "";
                exporting.value = false;
                return;
            }
            exportingText.value = t("shared.loading");
            await downloadSelectedPatientsAsZips(selectedMultiplePatients.value ?? [], saveTarget);
        } else {
            const filesForDownload: ProjectModel[] = (selectedPatientProjects.value ?? []).map(({ uuid, projectFilename }) => ({
                fileUuid: uuid,
                downloadFilename: projectFilename,
            }));

            await downloadAll(filesForDownload);
        }
        exporting.value = false;
    } else {
        const fileName = model.downloadFilename;
        exportingText.value = t("web-rg.downloadPreparing", { filename: fileName, current: 1, total: 1, percent: 0 });

        const onProgress = useThrottleFn((progress: number = 0) => {
            // keep UI under 100% until the file is actually saved
            const pct = Math.max(0, Math.min(99, Math.ceil(progress * 100) - 1));
            progressBarValue.value = pct;
            exportingText.value = t("web-rg.downloadProgress", { filename: fileName, current: 1, total: 1, percent: pct });
        }, 250);

        try {
            const response = await api.GetFileByUUID(model.fileUuid, onProgress);

            if (!exportingClosed.value) {
                downloadFile(fileName, new Blob([response.data], { type: "application/zip" }));
            }

            progressBarValue.value = 100;
            exportingText.value = t("web-rg.downloadedFile", { filename: fileName, current: 1, total: 1, percent: 100 });
        } catch (err) {
            exportingText.value = t("web-rg.downloadFailedTryAgain", { filename: fileName, current: 1, total: 1 });
            throw err;
        } finally {
            exporting.value = false;
        }
    }
};

userSettingsStore.prepareDefaults();

const selectedPatientArray = ref();
const hasSelectedPatient = computed(() => Boolean(selectedPatientArray.value));
const isSmartphoneListOnly = computed(() => isSmartphoneMode.value && !hasSelectedPatient.value);
const mobileSelectedDetailsCollapsed = ref(false);
const isSmartphoneCollapsedPatientDetails = computed(() => isSmartphoneMode.value && hasSelectedPatient.value && mobileSelectedDetailsCollapsed.value);
const shouldUseFullHeightPatientListContainer = computed(() => !isMobile.value || !mobileSelectedDetailsCollapsed.value);

const onMobileSelectedDetailsCollapsedChange = (isCollapsed: boolean) => {
    mobileSelectedDetailsCollapsed.value = isCollapsed;
};

const autoSelectFirst = computed(() => {
    if (isSmartphoneMode.value) {
        return false;
    }

    return !(route.params.id && !selectedPatientArray.value);
});

const viewportHeight = ref(typeof window !== "undefined" ? window.innerHeight : 0);
const smartphoneViewportOffset = 104;

const updateViewportHeight = () => {
    if (typeof window === "undefined") {
        return;
    }

    viewportHeight.value = window.innerHeight;
};

const availableSmartphoneHeight = computed(() => {
    return Math.max(viewportHeight.value - smartphoneViewportOffset, 0);
});

const mainViewGridStyle = computed<CSSProperties | undefined>(() => {
    if (!isSmartphoneMode.value) {
        return undefined;
    }

    return {
        minHeight: `${availableSmartphoneHeight.value}px`,
        height: "auto",
        overflowY: "visible",
        paddingBottom: "max(16px, env(safe-area-inset-bottom))",
    };
});

const selectedPatientName = computed(() => {
    const firstName = selectedPatientArray.value?.firstName ?? "";
    const lastName = selectedPatientArray.value?.lastName ?? "";

    return `${firstName} ${lastName}`.trim();
});

const patientTitleSizeClass = computed(() => {
    const smallViewport = viewportHeight.value < 1200;
    const threshold = smallViewport ? 59 : 107;

    return selectedPatientName.value.length > threshold ? "text-2xl" : "text-4xl";
});

async function openWeblikPcSelection() {
    const token = await userStore.getUserSessionToken();

    toWeblinkPcSelection(token ?? "");
}

const goToOpenTicket = async () => {
    const vendorId = GlobalConstants.VENDOR_NAME ?? "";
    const version = isDevelopment() ? import.meta.env.VITE_GIT_SHA ?? "" : GlobalConstants.BUILD_NUMBER;

    openSuppportTicket(username.value, vendorId, version);
};

const goToVideoTutorial = async () => {
    const vendorName = (await patientsStore.ensureVendorName()) ?? GlobalConstants.VENDOR_NAME;
    const url = await utilStore.getInfoUrl("VIDEO_TUTORIAL", false);
    toVideoTutorial(vendorName, url);
};

const goToRemoteSupport = async () => {
    const url = await utilStore.getInfoUrl("REMOTE_SUPPORT");
    toRemoteSupport(url);
};

type NavbarCallback = (event: ButtonEnum) => Promise<void>;

function subscribeNavbar(cbNavbar: NavbarCallback) {
    NavbarEventBus.on(NavbarNamedEvents.NAVBAR_BUTTON_CLICKED, cbNavbar);
}

function unSubscribeNavbar(cbNavbar: NavbarCallback) {
    NavbarEventBus.off(NavbarNamedEvents.NAVBAR_BUTTON_CLICKED, cbNavbar);
}

const onNavbarButtonClickedCB = async (event: ButtonEnum) => {
    if (event === ButtonEnum.PROFILE_INFO) {
        toUserDashboard();
    } else if (event === ButtonEnum.LOGOUT) {
        navbar.hideNavbar();
        await logout();
    } else if (event === ButtonEnum.RESET_PASSWORD) {
        toUserForgetPassoword();
    } else if (event === ButtonEnum.VIDEO_TUTORIAL) {
        await goToVideoTutorial();
    } else if (event === ButtonEnum.E_LEARNING) {
        warn(t("web-rg.notYetAvailable"));
    } else if (event === ButtonEnum.REMOTE_SUPPORT) {
        await goToRemoteSupport();
    } else if (event === ButtonEnum.RG_WEB) {
        info(t("web-rg.alreadyInRgWeb"));
    } else if (event === ButtonEnum.PC_LIST) {
        await openWeblikPcSelection();
    } else if (event === ButtonEnum.USER_SETTINGS) {
        await showSettingsDialog();
    } else if (event === ButtonEnum.OPEN_TICKET) {
        await goToOpenTicket();
    }
};

onBeforeUnmount(() => {
    if (typeof document !== "undefined") {
        document.documentElement.classList.remove("rg-smartphone-scroll-fix");
        document.body.classList.remove("rg-smartphone-scroll-fix");
    }

    if (typeof window === "undefined") {
        return;
    }

    window.removeEventListener("resize", updateViewportHeight);
});

const isAttachmentsTab = (tab: number) => tab === 1 || tab === 2 || tab === 3;

const loadAttachmentsForTab = async (tab: number, patientName?: string) => {
    if (!isAttachmentsTab(tab)) return;

    const resolvedPatientName = patientName ?? (route.params.id as string);
    if (!resolvedPatientName || loadingAttachemnts.value) return;

    if (lastAttachmentsPatientId.value === resolvedPatientName && lastAttachmentsTabIndex.value === tab && selectedPatientFileModel.value.length > 0) {
        return;
    }

    loadingAttachemnts.value = true;
    const requestId = ++attachmentsLoadRequestId.value;

    await patientsStore.loadPatientAttachments(resolvedPatientName);

    if (requestId === attachmentsLoadRequestId.value) {
        lastAttachmentsPatientId.value = resolvedPatientName;
        lastAttachmentsTabIndex.value = tab;
    }

    loadingAttachemnts.value = false;
};
let user: Ref<User | undefined> = ref(undefined);

onMounted(async () => {
    await auth.checkSession(WebCADModule.PLANNER);
    try {
        user.value = auth.getUser();
    } catch (err) {
        devLogger.warn("Failed to resolve authenticated user", err);
    }
    if (user.value) {
        const navbarModel = await GetNavbarModel(user.value, false);
        navbar.showNavbar(navbarModel);
    }
    ChangeBackground();

    if (typeof window === "undefined") {
        return;
    }

    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);

    await productionCenterStore.getProductionCenterList();

    if (patients.value.length === 0) {
        // Use the paginated version to load initial data
        await patientsStore.loadPatientsSlim();
        await patientsStore.updatePatientListNotifications();
    }

    if (route.params.id && !isSmartphoneMode.value) {
        const patientIndex = patients.value.findIndex((p: PatientListModel) => p.patientName === route.params.id);

        if (patientIndex === -1) {
            await selectFirst();
        } else {
            await patientSelectionChanged(patients.value[patientIndex], true);
        }
    }

    loading.value = false;

    if (patients.value.length > 0 && !isSmartphoneMode.value) {
        const routedPatient = route.params.id ? patients.value.find((p: PatientListModel) => p.patientName === route.params.id) : undefined;
        if (routedPatient) {
            selectedPatientArray.value = routedPatient;
        }
    }
    subscribeNavbar(onNavbarButtonClickedCB);

    await loadAttachmentsForTab(tabIndex.value, selectedPatientArray.value?.patientName);
});

watch(
    () => [hasOpenTicket.value, hasRemoteSupport.value],
    async () => {
        if (user.value != undefined) {
            const navbarModel = await GetNavbarModel(user.value, false);
            navbar.showNavbar(navbarModel);
        }
    },
);

onUnmounted(() => {
    unSubscribeNavbar(onNavbarButtonClickedCB);
});

watchEffect(() => {
    if (mainProject.value) {
        hideGrid.value = false;
    }
});

watchEffect(() => {
    if (typeof document === "undefined") {
        return;
    }

    const enableSmartphoneScrollFix = isSmartphoneMode.value;
    document.documentElement.classList.toggle("rg-smartphone-scroll-fix", enableSmartphoneScrollFix);
    document.body.classList.toggle("rg-smartphone-scroll-fix", enableSmartphoneScrollFix);
});

watch(
    () => selectedPatientFileModel.value,
    () => {
        devLogger.debug("selectedPatientFileModel 231 WATCH NEWW");
    },
);

watch(
    () => selectedPatientFull.value,
    async () => {
        devLogger.debug("selectedPatientFileModel 231 WATCH");

        if (!isAttachmentsTab(tabIndex.value)) return;

        lastAttachmentsPatientId.value = null;
        lastAttachmentsTabIndex.value = null;

        await patientsStore.emptyPatientAttachemtns();
        await loadAttachmentsForTab(tabIndex.value, selectedPatientFull.value?.name ?? selectedPatientArray.value?.patientName);
    },
);

watch(
    () => tabIndex.value,
    async (newTab) => {
        await loadAttachmentsForTab(newTab, selectedPatientArray.value?.patientName);

        if (newTab === 4) {
            const patient = patients.value.find((p) => p.patientId === selectedPatientArray.value?.patientId);

            if (!patient) return 0;

            if (mainProject.value?.projectUnreadNotificationsPerChannel !== 0) {
                await patientsStore.updateSeenNotifications(patient.patientId || "");
            }

            patient.projectUnreadNotificationsPerChannel = 0;
            if (selectedPatientArray.value?.patientId === patient.patientId) {
                selectedPatientArray.value.projectUnreadNotificationsPerChannel = 0;
            }
        }
    },
    { immediate: true },
);

watch(
    utilStore.$state,
    (
        state: {
            isMobileView: boolean;
            isTabletView: boolean;
            screenSIze: number;
            screenHeight: number;
            screenType: string;
            dataTableRowCount: number;
        } & PiniaCustomStateProperties<UtilModel>,
    ) => {
        isMobile.value = state.isMobileView;
        localStorage.setItem("piniaState", JSON.stringify(state));
    },
    { deep: true },
);

const updatePatients = async (studyInstanceUID: string) => {
    await patientsStore.loadPatientsSlim();
    patients.value = patientsStore.patients;
    for (let p of patients.value) {
        if (p.patientName == studyInstanceUID) {
            if (p.patientId) {
                files.projects = [];
                await patientSelectionChanged(p);
                router.push({
                    name: "Patients",
                    params: {
                        id: studyInstanceUID,
                    },
                });
                selectedPatientArray.value = patients.value[patients.value.findIndex((p: PatientListModel) => p.patientName === studyInstanceUID)];
            }
        }
    }
};

const updateProjectsPromise = async (patientId: string) => {
    const startTime = performance.now();
    devLogger.debug(`[updateProjectsPromise] ▶️ Started for patientId=${patientId} at ${new Date().toISOString()}`);

    const projectsToLoad = projectsModel.value.filter((p) => !p.imageBuffer && p.projectDirty === true && p.patientId === patientId);

    devLogger.debug(`[updateProjectsPromise] ▶️ projectsModel.value.length=${projectsModel.value.length} at ${new Date().toISOString()}`);
    let loadedCount = 0;

    const projectPromises = projectsToLoad.map((project) => {
        const projectStart = performance.now();
        devLogger.debug(`[updateProjectsPromise] Loading project ${project.id}...`);

        return patientsStore
            .loadProjectDataSlim(project.id)
            .then(() => {
                loadedCount++;
                const projectEnd = performance.now();
                devLogger.debug(`[updateProjectsPromise] ✅ Loaded project ${project.id} in ${(projectEnd - projectStart).toFixed(2)} ms`);
            })
            .catch((err) => {
                const projectEnd = performance.now();
                devLogger.error(`[updateProjectsPromise] ❌ Failed to load project ${project.id} after ${(projectEnd - projectStart).toFixed(2)} ms`, err);
            });
    });

    // Run all project loads in parallel
    return Promise.allSettled(projectPromises).then((results) => {
        const endTime = performance.now();
        const failed = results.filter((r) => r.status === "rejected").length;
        devLogger.debug(`[updateProjectsPromise] 🏁 Finished. Loaded ${loadedCount}/${projectsToLoad.length} (failed ${failed}) in ${(endTime - startTime).toFixed(2)} ms`);
        return {
            loaded: loadedCount,
            failed,
            total: projectsToLoad.length,
            elapsedMs: +(endTime - startTime).toFixed(2),
        };
    });
};

let patientNameFromSelection = "";
const getLegacyProjectForSelectedPatient = () => {
    const selectedPatientName = selectedPatientArray.value?.patientName ?? selectedPatient?.patientName;
    if (!selectedPatientName) {
        return undefined;
    }

    return legacyProjectsModel.value.find((project) => project.patientName === selectedPatientName);
};

const projectNotifications = computed({
    get() {
        if (selectedPatientProjects.value.length > 0) {
            return mainProject.value?.notifications ?? selectedPatientNotifications.value ?? [];
        }

        return getLegacyProjectForSelectedPatient()?.notifications ?? selectedPatientNotifications.value ?? [];
    },
    set(notifications) {
        if (selectedPatientProjects.value.length > 0 && mainProject.value) {
            mainProject.value.notifications = notifications;
            return;
        }

        const legacyProject = getLegacyProjectForSelectedPatient();
        if (legacyProject) {
            legacyProject.notifications = notifications;
            return;
        }

        selectedPatientNotifications.value = notifications ?? [];
    },
});

const patientSelectionChanged = async (model: PatientListModel | null, projectDirty: boolean = false) => {
    loading.value = true;
    files.projects = [];

    if (!model) {
        return;
    }

    if (model.patientName && model.patientName === selectedPatientArray.value?.patientName && !projectDirty) {
        loading.value = false;
        return;
    }

    selectedPatientArray.value = model;
    selectedPatient = model;
    selectedPatientArray.value = model;
    patientNameFromSelection = model.patientName;

    selectedPatientDoctor.value = doctors.value.find((d) => d.id === Number(selectedPatient?.extra.doctorID));

    if (model.patientId) {
        await patientsStore.getPatientProjectsChanged(model.patientId);
        await updateProjectsPromise(model.patientId);

        if (isAttachmentsTab(tabIndex.value)) {
            lastAttachmentsPatientId.value = null;
            lastAttachmentsTabIndex.value = null;

            await patientsStore.emptyPatientAttachemtns();
            await loadAttachmentsForTab(tabIndex.value, model.patientName);
        }
    }

    loading.value = false;

    forceRerenderProjects.value += 1;
    forceUpdateChat.value = Math.random() * 100;

    if (selectedPatientProjects.value.length > 0) {
        const attachmentProject = selectedPatientProjects.value.find((p) => p?.type === ProjectFileType.ProjectAttachment);

        mainProject.value = attachmentProject || selectedPatientProjects.value[0];
    } else {
        mainProject.value = {};
    }
};

const onSendMessage = async (data: ChatMessageModel) => {
    const patientName = (data.projectName ?? mainProject.value?.patientName ?? selectedPatientArray.value?.patientName ?? "").trim();
    if (!patientName) {
        warn(t("web-rg.missingPatientNameForDeletion"));
        return;
    }

    await patientsStore.sendChatMessage(patientName, data.text, false, NotificationChannelType.CHAT);
};

type NotificationUploadPayload = {
    projectName?: string;
    upload: WeblinkFileUploadEmitModel;
};

const getNotificationUploadType = (file: File): ProjectFileType => {
    const fileName = file.name.toLowerCase();

    if (file.type.startsWith("image/")) {
        return ProjectFileType.Image;
    }

    if (isMeshFileFormat(file.name)) {
        return ProjectFileType.STL;
    }

    if (fileName.endsWith(".zip")) {
        return ProjectFileType.ProjectAttachment;
    }

    return ProjectFileType.Document;
};

const uploadNotificationStlPreview = async (projectName: string, file: File) => {
    const data = await file.arrayBuffer();
    const screenshot = await takeStlFileScreenshot({
        data,
        background: [30, 30, 30, 255],
        color: [255, 255, 255],
        width: 2560,
        height: 1440,
        isPly: file.name.toLowerCase().endsWith(".ply"),
        isObj: file.name.toLowerCase().endsWith(".obj"),
    });

    if (!screenshot) return;

    const base64Response = await fetch(screenshot);
    const blob = await base64Response.blob();
    await api.uploadFile(projectName, `${file.name}.thumb`, ProjectFileType.STL_Preview, blob);
};

const uploadNotificationFile = async (projectName: string, file: File) => {
    const fileType = getNotificationUploadType(file);

    if (fileType === ProjectFileType.STL) {
        await uploadNotificationStlPreview(projectName, file);
    }

    await api.uploadFile(projectName, file.name, fileType, file);
};

const onUploadFileAndShareToChat = async (model: NotificationUploadPayload) => {
    const files = model.upload?.files ?? [];
    if (files.length === 0) return;

    const projectName = (model.projectName ?? mainProject.value?.patientName ?? selectedPatientArray.value?.patientName ?? "").trim();
    if (!projectName) {
        warn("Missing project name for chat upload");
        return;
    }

    uploadingAttachment.value = true;
    try {
        for (const file of files) {
            await uploadNotificationFile(projectName, file);
        }

        if (model.upload.data?.shareToChat) {
            const firstFile = files[0];
            if (firstFile) {
                await patientsStore.sendMediaMessage(projectName, firstFile.name, model.upload.data.message ?? "");
            }
        }

        await patientsStore.updatePatientListNotifications();

        const selectedPatientName = selectedPatientArray.value?.patientName;
        if (selectedPatientName && selectedPatientName === projectName) {
            await patientsStore.loadPatientAttachments(selectedPatientName);
        }
    } catch (err) {
        exception("Error while uploading notification attachment:", err as Exception);
    } finally {
        uploadingAttachment.value = false;
    }
};

const addPatient = () => {
    dialog.open(AddPatientDialog, {
        props: {
            closable: true,
            modal: true,
            dismissableMask: true,
            header: t("web-rg.dataImport"),
            style: { width: "40vw" },
            breakpoints: { "640px": "95vw" },
        },
        emits: {
            onNextStep: (e: formData) => {
                next(e);
            },
        },
    });
};

const editPatient = async () => {
    // await patientsStore.getUserDoctorList();

    dialog.open(EditPatientDialog, {
        props: {
            closable: true,
            modal: true,
            dismissableMask: true,
            header: t("web-rg.editPatient"),
            style: { width: "50vw" },
            breakpoints: { "640px": "95vw" },
        },
        data: {
            patientData: patients.value.find((p: PatientListModel) => p.patientName == selectedPatient?.patientName),
            doctors: doctors.value,
        },
        emits: {
            onPatientUpdated: async () => {
                await patientsStore.loadPatientsSlim();
                await patientsStore.updatePatientListNotifications();

                changeSelectedPatient.value = patients.value.find((p: PatientListModel) => p.patientName == selectedPatient?.patientName) as PatientListModel;

                devLogger.debug("patientSelectionChanged changeSelectedPatient.value: ", changeSelectedPatient.value);
                devLogger.debug("patientSelectionChanged changeSelectedPatient.value selectedPatientArray: ", selectedPatientArray.value);

                //await patientSelectionChanged(changeSelectedPatient.value);

                await selectFirst();
            },
        },
    });
};

const importData = async (data?: formData, addNewPatientFlag?: boolean) => {
    let fList: FileElement[] = [];
    if (selectedPatient && !addNewPatientFlag) {
        const r = await api.getProjectFileList<FileElement[]>(selectedPatient.patientName);
        fList = r.data;
    }
    dialog.open(ImportProjectDialog, {
        props: {
            modal: true,
            closeOnEscape: false,
            dismissableMask: true,
            style: { width: "90vw" },
            header: t("web-rg.dataImport"),
        },
        data: {
            projects: fList,
            selectedPatient: selectedPatient,
            patientData: data,
            addNewPatientFlag: addNewPatientFlag,
        },
        emits: {
            onUpdatePatients: async (uid: string) => {
                await selectFirst();
                await updatePatients(uid);
            },
            onDone: async () => {
                await selectFirst();
            },
        },
    });
};

const showSettingsDialog = async () => {
    if (!license.value) {
        return;
    }

    await patientsStore.ensureVendorName();
    const aboutModel = getAboutInfoModel(license.value, GlobalConstants.VENDOR_ID);
    dialog.open(IPAboutInfoComponent, {
        props: {
            breakpoints: { "1900px": "50vw" },
            modal: true,
            dismissableMask: true,
            style: { width: "40vw" },
            header: t("shared.settings"),
        },
        data: {
            model: aboutModel,
        },
    });
};

const onOpenSTLClick = () => {
    router.push({
        name: "ImplantPlannerProjectLoaderView",
        params: {
            projectHash: mainProject.value?.id,
        },
    });
};

const projectObjects = computed(() => {
    return selectedPatientFileModel.value.filter((file: ImageFileModel) => file.type === ProjectFileType.STL);
});

const projectDocuments = computed(() => {
    return selectedPatientFileModel.value.filter((file: ImageFileModel) => file.type === ProjectFileType.Document);
});

const projectImages = computed(() => {
    return selectedPatientFileModel.value.filter((file: ImageFileModel) => file.type === ProjectFileType.Image);
});

const projectCount = computed(() => {
    if (selectedPatientProjects.value) {
        return selectedPatientProjects.value.length.toString();
    }
    return "";
});

const unreadNotificationsCount = computed(() => {
    if (!mainProject.value || Object.keys(mainProject.value).length === 0) {
        if (selectedPatientArray.value) {
            return selectedPatientArray.value.unreadNotifications;
        }
        return 0;
    } else {
        const patient = patients.value.find((p) => p.patientId === mainProject.value?.patientId);
        if (!patient) return 0;

        return (patient.orderUnreadNotificationsPerChannel ?? 0) + (patient.projectUnreadNotificationsPerChannel ?? 0);
    }
});

const toUnreadNotificationCount = (unread: number | Record<string, number | null | undefined> | null | undefined) => {
    if (typeof unread === "number") return unread;
    if (unread && typeof unread === "object") {
        return Object.values(unread).reduce<number>((sum, value) => sum + (typeof value === "number" ? value : 0), 0);
    }
    return 0;
};

type OrderNotificationEntry = {
    orderNotifications: any;
    projectId: number | undefined;
    caseNumber: string;
    weblinkProjectName: string | undefined;
    unreadNotificationsPerChannel: number;
};

const updateSeenNotifications = async (data: number) => {
    // update seen for weblink order
    const currentUnread = selectedPatientArray.value.orderUnreadNotificationsPerChannel ?? 0;
    if (currentUnread > 0) {
        await patientsStore.updateSeenNotifications(selectedPatientArray.value.patientId || "", data);
        selectedPatientProjects.value.forEach((project) => {
            if (project.weblinkOrderData?.projectId === data) {
                project.weblinkOrderData.unreadNotificationsPerChannel = 0;
            }
        });
        const nextUnread = orderNotifications.value.reduce((sum: number, entry: OrderNotificationEntry) => sum + toUnreadNotificationCount(entry.unreadNotificationsPerChannel), 0);
        selectedPatientArray.value.orderUnreadNotificationsPerChannel = nextUnread;
        const patient = patients.value.find((p) => p.patientId === selectedPatientArray.value.patientId);
        if (patient) {
            patient.orderUnreadNotificationsPerChannel = nextUnread;
        }
    }
};

const tabMenuItems = computed(
    () =>
        [
            { label: t("shared.project"), badge: projectCount.value ?? undefined, index: 0 },
            { label: t("shared.dObjects"), badge: undefined, index: 1 },
            { label: t("web-rg.pictures"), badge: undefined, index: 2 },
            { label: t("shared.documents"), badge: undefined, index: 3 },
            {
                label: t("shared.notifications"),
                badge: unreadNotificationsCount.value > 0 ? unreadNotificationsCount.value.toString() : undefined,
                index: 4,
            },
        ] satisfies BadgeModel[],
);

const orderNotifications = computed<OrderNotificationEntry[]>(() => {
    devLogger.debug("orderNotifications653: ", selectedPatientProjects.value);

    const seenCaseNumbers = new Set<string>();

    return selectedPatientProjects.value.reduce<OrderNotificationEntry[]>((acc, project) => {
        const caseNumber = project.weblinkOrderData?.caseNumber;
        if (!caseNumber || seenCaseNumbers.has(caseNumber)) return acc;

        seenCaseNumbers.add(caseNumber);
        acc.push({
            orderNotifications: project.orderNotifications,
            projectId: project.weblinkOrderData?.projectId,
            caseNumber,
            weblinkProjectName: project.weblinkOrderData?.weblinkProjectName,
            unreadNotificationsPerChannel: toUnreadNotificationCount(project.weblinkOrderData?.unreadNotificationsPerChannel),
        });

        return acc;
    }, []);
});

const mainProjectOrderNotifications = computed({
    get: () => mainProject.value?.orderNotifications ?? [],
    set: (notifications) => {
        if (mainProject.value) {
            mainProject.value.orderNotifications = notifications;
        }
    },
});

const isLoadingOrMultipleSelections = computed(() => {
    return loading.value || selectMultiple.value;
});

const clearPatientProjectSelection = () => {
    selectedPatient = null;
    selectedPatientArray.value = undefined;
    selectedMultiplePatients.value = [];
    selectMultiple.value = false;
    selectedPatientDoctor.value = undefined;
    changeSelectedPatient.value = undefined;
    mainProject.value = null;
    selectedOrderId.value = undefined;

    patientsStore.clearSelectedPatientsAndProjects();
    patients.value = [];
    projectsModel.value = [];
    legacyProjectsModel.value = [];
};

const logout = async () => {
    clearPatientProjectSelection();

    await auth.logout();
    redirectToLogin(true);
};
</script>

<style scoped>
#main-view-grid {
    display: grid;
    gap: 16px;
    padding: 0px 16px 16px 16px;
    height: calc(100vh - 104px);
    height: calc(100dvh - 104px);
    box-sizing: border-box;
}

@media (min-width: 1900px) {
    #main-view-grid {
        grid-template-columns: 38% 1fr;
        grid-template-rows: 1fr;
        overflow: hidden;
    }
}

@media (min-width: 1600px) and (max-width: 1899px) {
    #main-view-grid {
        grid-template-columns: 47% 1fr;
        grid-template-rows: 1fr;
        overflow: hidden;
    }
}

@media (max-width: 1599px) {
    #main-view-grid {
        /*grid-template-columns: 1fr;
        grid-template-rows: minmax(0, 60%) minmax(0, 40%);*/
        grid-template-columns: 50% 1fr;
        grid-template-rows: 1fr;
        overflow: hidden;
    }
}

@media (max-width: 900px) {
    #main-view-grid {
        grid-template-columns: 1fr;
        grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
        overflow-y: auto;
    }

    #main-view-grid.smartphone-collapsed-patient-details {
        grid-template-rows: auto minmax(0, 1fr);
    }

    #main-view-grid.smartphone-list-only {
        grid-template-rows: minmax(0, 1fr);
    }

    #main-view-grid.smartphone-mode {
        grid-template-rows: auto auto;
        height: auto;
        min-height: 0;
        overflow: visible;
        align-content: start;
    }

    #main-view-grid.smartphone-mode.smartphone-list-only {
        grid-template-rows: auto;
    }

    #main-view-grid.smartphone-mode .main-view-grid-column,
    .smartphone-scroll-shell,
    .smartphone-scroll-pane,
    .smartphone-scroll-content {
        height: auto !important;
        min-height: 0;
        max-height: none !important;
        overflow: visible !important;
        -webkit-overflow-scrolling: touch;
    }

    .rg-project-list-mobile-flush {
        width: calc(100% + 32px);
        margin-left: -16px;
        margin-right: -16px;
        overflow-x: hidden;
    }
}

.main-view-grid-column {
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    align-items: stretch;
    justify-content: flex-start;
}

.smartphone-unsupported-row {
    position: relative;
    margin: 0 16px 12px;
    padding: 10px 36px 10px 14px;
    border: 1px solid #ffcf7a;
    border-radius: 10px;
    background: #fff8e8;
    color: #7c4a00;
    font-size: 0.9rem;
    line-height: 1.3;
    text-align: center;
}

.smartphone-unsupported-close {
    position: absolute;
    top: 50%;
    right: 8px;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    transform: translateY(-50%);
    background: transparent;
    color: inherit;
    cursor: pointer;
}

.smartphone-unsupported-close:focus-visible {
    outline: 2px solid #7c4a00;
    outline-offset: 1px;
}

:deep(.rg-confirm-message) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
}

:deep(.rg-confirm-message-text) {
    white-space: pre-line;
    text-align: center;
}

:deep(.rg-confirm-footer) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
}

:global(.max-patients-confirm [data-pc-name="pcacceptbutton"]) {
    display: none !important;
}

:deep(.p-button-outlined) {
    background: rgba(255, 255, 255, 0.06);
    border: 1px var(--p-rg-outlined-button-border-color) solid !important;
    color: var(--p-rg-component-text-color) !important;
}

.rg-confirm-icon {
    font-size: 2rem;
    line-height: 2.5rem;
    margin-top: -0.5rem;
}
</style>

<template>
    <ConfirmDialog group="deleteStudy" :closeOnEscape="true" :dismissableMask="true">
        <template #message="slotProps">
            <div class="flex w-full items-start gap-3">
                <i v-if="slotProps.message.icon" :class="['rg-confirm-icon', slotProps.message.icon]" aria-hidden="true"></i>
                <p style="white-space: pre-line; text-align: center; margin: 0">{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>
    <ConfirmDialog group="maxPatientsSelection" class="max-patients-confirm" :closeOnEscape="true" :dismissableMask="true">
        <template #message="slotProps">
            <div class="flex w-full items-start gap-3">
                <i v-if="slotProps.message.icon" :class="['rg-confirm-icon', slotProps.message.icon]" aria-hidden="true"></i>
                <p style="white-space: pre-line; text-align: center; margin: 0">{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>
    <ConfirmDialog group="deleteProjectFile" :closeOnEscape="true" :dismissableMask="true" />
    <Dialog
        v-model:visible="exporting"
        :closable="true"
        :draggable="false"
        modal
        :header="$t('shared.exporting')"
        :style="{ width: exportDialogWidth }"
        @update:visible="exportClosedClicked">
        <m-loading :strokeWidth="5">
            <span>{{ exportingText }}</span>
        </m-loading>
    </Dialog>
    <div
        v-if="loadingFlag"
        id="loadingDiv"
        class="flex flex-col gap-6 items-center justify-center"
        :key="progressBarRefresh"
        :style="{
            position: 'absolute',
            inset: '0px',
            background: 'rgba(91, 91, 91, 0.5)',
            pointerEvents: 'all',
            cursor: 'wait',
            zIndex: '20',
        }">
        <m-loading :strokeWidth="5">
            <div style="text-align: center; color: white; text-shadow: 0 1px 1px black">{{ loadingText }}</div>
            <div v-if="progressBarFlag" class="mt-20" style="width: 20vw">
                <ProgressBar :value="progressBarValue" :showValue="false" :key="progressBarRefresh" style="height: 2rem" :pt="{ value: { style: { transition: 'none' } } }">
                    <span style="color: white">{{ progressBarValue.toFixed(0) }} %</span>
                </ProgressBar>
            </div>
        </m-loading>
    </div>
    <div v-if="showSmartphoneUnsupportedNotice" class="smartphone-unsupported-row">
        {{ $t("shared.smartphonesNotSupportedUseTabletOrDesktop") }}
        <button type="button" class="smartphone-unsupported-close" :title="$t('shared.close')" :aria-label="$t('shared.close')" @click="dismissSmartphoneUnsupportedNotice">
            <i class="pi pi-times" aria-hidden="true"></i>
        </button>
    </div>
    <div
        id="main-view-grid"
        :class="{
            'smartphone-mode': isSmartphoneMode,
            'smartphone-list-only': isSmartphoneListOnly,
            'smartphone-collapsed-patient-details': isSmartphoneCollapsedPatientDetails,
        }"
        :style="mainViewGridStyle">
        <div :class="['main-view-grid-column', { 'smartphone-scroll-shell': isSmartphoneMode }]" :style="isSmartphoneCollapsedPatientDetails ? { height: 'auto' } : undefined">
            <MContainerColumn
                :class="[
                    'w-full min-h-0 gap-4 overflow-hidden',
                    { 'h-full': shouldUseFullHeightPatientListContainer && !isSmartphoneMode, 'smartphone-scroll-pane': isSmartphoneMode },
                ]">
                <!--                <MContainer class="gap-2-md-screen">-->
                <!--                    <RgLogoUserDropdown :username="username" />-->
                <!--                </MContainer>-->
                <MContainer
                    :class="['min-h-0 overflow-hidden', { 'h-full': shouldUseFullHeightPatientListContainer && !isSmartphoneMode, 'smartphone-scroll-pane': isSmartphoneMode }]">
                    <MContainerColumn
                        :class="[
                            'w-full min-h-0 rg-main-container',
                            { 'h-full': shouldUseFullHeightPatientListContainer && !isSmartphoneMode, 'p-2': isSmartphoneMode, 'smartphone-scroll-pane': isSmartphoneMode },
                        ]">
                        <MContainerRow>
                            <RgPatientListHeader
                                v-model:searchString="searchString"
                                v-model:selectMultiple="selectMultiple"
                                v-model:filterDateRange="filterDateRange"
                                v-model:selectedPatients="selectedMultiplePatients"
                                :showMultipleSelection="!isMobile"
                                :showSearch="!isMobile"
                                @addPatient="addPatient"
                                @deleteClicked="onDelete"
                                @exportClicked="exportClicked"
                                @filtersCleared="onPatientListFiltersCleared" />
                        </MContainerRow>
                        <MContainerGrow
                            :class="[
                                'w-full min-h-0 overflow-hidden pt-2 box-border',
                                {
                                    'h-full': shouldUseFullHeightPatientListContainer && !isSmartphoneMode,
                                    'horizontal-padding': isSmartphoneMode,
                                    'smartphone-scroll-pane': isSmartphoneMode,
                                },
                            ]"
                            :style="shouldUseFullHeightPatientListContainer ? undefined : { flexGrow: 0 }">
                            <RgPatientList
                                v-if="showPatientList"
                                :class="['w-full', { 'h-full': shouldUseFullHeightPatientListContainer && !isSmartphoneMode, 'smartphone-scroll-content': isSmartphoneMode }]"
                                :patients="patients"
                                :selectedPatient="selectedPatientArray"
                                :loading="loading"
                                :autoSelectFirst="autoSelectFirst"
                                :page="patientListQuery.page"
                                :totalPatientCount="totalPatientCount"
                                v-model:searchString="searchString"
                                v-model:selectMultiple="selectMultiple"
                                v-model:selectedPatients="selectedMultiplePatients"
                                @pageChange="onPatientListPageChange"
                                @sortChange="onPatientListSortChange"
                                @pageSizeChange="onPatientListPageSizeChange"
                                @searchChange="onPatientListSearchChange"
                                @shareClicked="onShareClicked"
                                @importClicked="importData"
                                @deleteClicked="onDelete"
                                @editClicked="editPatient"
                                @exportClicked="exportClicked"
                                @mobile-selected-details-collapsed-change="onMobileSelectedDetailsCollapsedChange"
                                @selectionChanged="patientSelectionChanged" />
                        </MContainerGrow>
                    </MContainerColumn>
                </MContainer>
            </MContainerColumn>
        </div>
        <div v-if="!isSmartphoneListOnly" :class="['main-view-grid-column', { 'smartphone-scroll-shell': isSmartphoneMode }]">
            <MContainerColumn :class="['w-full h-full gap-4 overflow-y-auto', { 'smartphone-scroll-pane': isSmartphoneMode }]">
                <MContainer :class="['h-full overflow-y-auto', { 'smartphone-scroll-pane': isSmartphoneMode }]">
                    <MContainerColumn :class="['w-full h-full rg-main-container', { 'p-2': isSmartphoneMode, 'smartphone-scroll-pane': isSmartphoneMode }]">
                        <!-- header -->
                        <MContainer>
                            <MContainerRow class="gap-2" style="border-bottom: 1px var(--p-rg-border-color) solid; min-height: 6.36rem !important">
                                <MContainer :class="['ml-4 align-center', { 'pt-4': patientTitleSizeClass === 'text-2xl', 'opacity-50': selectMultiple }]">
                                    <RgTitle v-if="!loading" id="lblPatientName" class="flex grow" :size-class="patientTitleSizeClass">
                                        {{ selectMultiple ? t("web-rg.multiSelectInThePatientListIsCurrentlyEnabled") : selectedPatientName }}
                                    </RgTitle>
                                </MContainer>
                                <MContainer class="align-center">
                                    <RgSubTitle v-if="!loading" class="mt-2 ml-2">
                                        {{ selectedPatientDoctor?.firstName ?? "" }}
                                        {{ selectedPatientDoctor?.lastName ?? "" }}
                                    </RgSubTitle>
                                    <span v-else>
                                        <Skeleton width="15rem" height="4rem" class="mb-2" />
                                    </span>
                                </MContainer>
                            </MContainerRow>
                        </MContainer>
                        <!-- content -->
                        <MContainerGrow :class="['w-full h-full overflow-y-auto horizontal-padding', { 'smartphone-scroll-pane': isSmartphoneMode }]">
                            <MContainerColumn class="gap-5 pt-4 w-full">
                                <RgButtonMenu v-model:tab-index="tabIndex" :tab-menu-items="tabMenuItems" v-model:disabled="isLoadingOrMultipleSelections" />
                                <MContainer
                                    v-if="routeName === NamedRoutes.Patients"
                                    :class="[
                                        'h-full w-full overflow-y-auto',
                                        { 'rg-project-list-mobile-flush': isSmartphoneMode && tabIndex === 0, 'smartphone-scroll-pane': isSmartphoneMode },
                                    ]">
                                    <RgProjectList
                                        v-if="tabIndex == 0"
                                        :class="['w-full overflow-y-auto', { 'h-full': !isSmartphoneMode, 'smartphone-scroll-content': isSmartphoneMode }]"
                                        v-model:projects="selectedPatientProjects"
                                        :sharedWith="selectedPatientArray?.shares"
                                        :username="username"
                                        :loading="isLoadingOrMultipleSelections"
                                        :key="forceRerenderProjects"
                                        v-model:multipleSelected="selectMultiple"
                                        v-model:hasLegacyProject="hasLegacyProject"
                                        @reload-patient-store="updateProj"
                                        @segmentationTriggered="handleSegmentationTriggered"
                                        @export-clicked="exportClicked"
                                        @order-notifications-clicked="onOrderNotificationsClicked" />
                                    <RgFileListView
                                        v-if="tabIndex == 1"
                                        :class="['w-full overflow-y-auto', { 'h-full': !isSmartphoneMode, 'smartphone-scroll-content': isSmartphoneMode }]"
                                        :show-share="true"
                                        :show-open="false"
                                        :model="projectObjects"
                                        v-model:objects="projectObjects"
                                        :key="forceRerenderProjects"
                                        @open="onOpenSTLClick"
                                        @delete="onDeleteAttachment" />
                                    <RgFileListView
                                        v-if="tabIndex == 2"
                                        :class="{ 'smartphone-scroll-content': isSmartphoneMode }"
                                        :show-open="true"
                                        :show-share="true"
                                        :key="forceRerenderProjects"
                                        :model="projectImages"
                                        @delete="onDeleteAttachment" />
                                    <RgFileListView
                                        v-if="tabIndex == 3"
                                        :class="{ 'smartphone-scroll-content': isSmartphoneMode }"
                                        :show-open="true"
                                        :show-share="false"
                                        :model="projectDocuments"
                                        @delete="onDeleteAttachment" />
                                    <RgNotificationSelectionTab
                                        v-if="tabIndex == 4"
                                        v-model:projectNotifications="projectNotifications"
                                        v-model:orderNotifications="mainProjectOrderNotifications"
                                        v-model:ordersNotifications="orderNotifications"
                                        v-model:dateFormat="dateFormat"
                                        v-model:selectedOrderId="selectedOrderId"
                                        v-model:mainProject="mainProject"
                                        v-model:loading="loading"
                                        v-model:uploadingAttachment="uploadingAttachment"
                                        :isUserWeblinkOperator="true"
                                        :hasLegacyProject="hasLegacyProject"
                                        :selectedPatientName="selectedPatientArray?.patientName ?? ''"
                                        :key="forceUpdateChat"
                                        :class="['grow w-full', { 'smartphone-scroll-content': isSmartphoneMode }]"
                                        @updateSeenNotifications="updateSeenNotifications"
                                        @sendMessage="onSendMessage"
                                        @openSTL="onOpenSTLClick"
                                        @uploadFileAndShareToChat="onUploadFileAndShareToChat" />
                                </MContainer>
                                <MContainer
                                    v-else
                                    :class="[
                                        'h-full w-full overflow-y-auto',
                                        { 'rg-project-list-mobile-flush': isSmartphoneMode && tabIndex === 0, 'smartphone-scroll-pane': isSmartphoneMode },
                                    ]">
                                    <RgProjectList
                                        v-if="tabIndex == 0"
                                        :class="['w-full overflow-y-auto pt-2', { 'h-full': !isSmartphoneMode, 'smartphone-scroll-content': isSmartphoneMode }]"
                                        :projects="selectedPatientProjects"
                                        :sharedWith="selectedPatientArray?.shares"
                                        :key="forceRerenderProjects"
                                        @reload-patient-store="updateProj"
                                        @segmentationTriggered="handleSegmentationTriggered"
                                        @order-notifications-clicked="onOrderNotificationsClicked"
                                        @export-to-alfred="exportToAlfredTriggered"
                                        :exportToAlfred="exportToAlfred" />
                                </MContainer>
                            </MContainerColumn>
                        </MContainerGrow>
                    </MContainerColumn>
                </MContainer>
            </MContainerColumn>
        </div>
    </div>
    <div v-show="divFlag" ref="vtkContainer" style="margin-top: 200px; width: 800px; height: 600px" />
</template>
